/**
 * Cognito display name extraction utility
 *
 * Extracts a human-readable display name from AWS Cognito user attributes.
 * Cognito stores user profile data as key-value `Attributes` arrays rather
 * than structured fields, so this utility provides a consistent way to
 * resolve `given_name` and `family_name` into a display string.
 * @module utils/getCognitoDisplayName
 */

/**
 * Extracts a display name from Cognito user attributes
 * @param attributes - Array of Cognito user attribute key-value pairs
 * @param fallback - Fallback value when no name attributes are found
 * @returns Display name string (e.g., "John Doe") or fallback
 */
export const getCognitoDisplayName = (
  attributes:
    | readonly { readonly Name: string; readonly Value: string }[]
    | null
    | undefined,
  fallback = "Unknown"
): string => {
  const firstName = attributes?.find(attr => attr.Name === "given_name")?.Value;
  const lastName = attributes?.find(attr => attr.Name === "family_name")?.Value;
  const email = attributes?.find(attr => attr.Name === "email")?.Value;

  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return fullName || email || fallback;
};
